/**
 * Query params for `GET /api/v1/companies/:id/matches` list filtering.
 * Values are raw strings; {@link useQueryPagination} URL-encodes them when building the request.
 */
export const COMPANY_MATCHES_LIST_FILTER_KEYS = {
  status: 'status',
  /** Comma-separated `company_job_posting.resource_id` values (same as match filter option ids). */
  companyJobPostingIds: 'company_job_posting_ids',
} as const;

/**
 * API status values for the matches-page "In progress" / "In Bearbeitung" stat card filter
 * (contacted + interview + on_hold). Must stay aligned with MatchCountDisplay pipeline metrics.
 */
export const COMPANY_MATCH_IN_PIPELINE_STATUS_IDS = ['contacted', 'interview', 'on_hold'] as const;

/**
 * Status set applied by the matches page "In progress" stat card (all statuses except `accepted`),
 * in the same order as {@link useMatchesSorting#statusOptions} with `accepted` removed.
 */
export const MATCHES_PAGE_IN_PROGRESS_STATUS_IDS: readonly string[] = [
  'contacted',
  'on_hold',
  'interview',
  'hired',
  'rejected',
] as const;

export type MatchesPageActiveStatFilter = 'all' | 'pending' | 'inProgress' | 'hired' | null;

function sortedStatusIdListsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((id, i) => id === sb[i]);
}

/**
 * Which matches-page stat card filter the current `job` / `status` query params match, if any.
 * Align with click handlers in {@link MatchesPage} / {@link useMatchesSorting#filterBy}.
 */
export function getActiveMatchesPageStatFilter(
  jobCsv: string | undefined,
  statusCsv: string | undefined
): MatchesPageActiveStatFilter {
  const jobIds = (jobCsv?.split(',') ?? [])
    .map((s) => s.trim())
    .filter(Boolean);
  const statusIds = (statusCsv?.split(',') ?? [])
    .map((s) => s.trim())
    .filter(Boolean);

  if (jobIds.length === 0 && statusIds.length === 0) {
    return 'all';
  }
  if (statusIds.length === 1 && statusIds[0] === 'accepted') {
    return 'pending';
  }
  if (statusIds.length === 1 && statusIds[0] === 'hired') {
    return 'hired';
  }
  if (sortedStatusIdListsEqual(statusIds, [...MATCHES_PAGE_IN_PROGRESS_STATUS_IDS])) {
    return 'inProgress';
  }
  return null;
}

export function buildCompanyMatchesFilterParamsRecord(
  jobPostingResourceIdsCsv: string | undefined,
  statusCsv: string | undefined
): Record<string, string> | undefined {
  const statuses = statusCsv?.split(',').map((s) => s.trim()).filter(Boolean) ?? [];
  const jobPostingIds =
    jobPostingResourceIdsCsv?.split(',').map((j) => j.trim()).filter(Boolean) ?? [];
  if (!statuses.length && !jobPostingIds.length) {
    return undefined;
  }
  const out: Record<string, string> = {};
  if (statuses.length) {
    out[COMPANY_MATCHES_LIST_FILTER_KEYS.status] = statuses.join(',');
  }
  if (jobPostingIds.length) {
    out[COMPANY_MATCHES_LIST_FILTER_KEYS.companyJobPostingIds] = jobPostingIds.join(',');
  }
  return out;
}

/** Query string (no leading `?`) for list/summary endpoints that share the same filters. */
export function buildCompanyMatchesListFilterQueryString(
  jobPostingResourceIdsCsv: string | undefined,
  statusCsv: string | undefined
): string {
  const record = buildCompanyMatchesFilterParamsRecord(jobPostingResourceIdsCsv, statusCsv);
  if (!record) {
    return '';
  }
  return Object.entries(record)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}
